import React, { useEffect, useState } from "react";
import { FaSave } from "react-icons/fa";
import { formatDate } from "../../utils/formatDate";

const Carriers = () => {
  const [carriers, setCarriers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [modalData, setModalData] = useState(null); // Holds data for add/edit modals
  const [deleteId, setDeleteId] = useState(null); // ID of the carrier to be deleted
  const [editMode, setEditMode] = useState(false); // Toggle for edit mode
  const apiUrl = process.env.REACT_APP_BASE_URL;

  // Fetch carriers from API
  const fetchCarriers = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`${apiUrl}/api/carriers`);
      if (!response.ok) {
        throw new Error("Failed to fetch Carriers");
      }
      const data = await response.json();
      setCarriers(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err.message || "Failed to fetch data");
      setCarriers([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCarriers();
  }, []);

  // Handle adding/updating carrier
  const handleSave = async (carrier) => {
    try {
      const url = editMode
        ? `${apiUrl}/api/carriers/${carrier.id}`
        : `${apiUrl}/api/carriers`;
      const method = editMode ? "PUT" : "POST";

      await fetch(url, {
        method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(carrier),
      });
      fetchCarriers(); // Refresh list
      setModalData(null); // Close modal
    } catch (err) {
      setError("Error saving carrier. Please try again.");
    }
  };

  // Handle deleting carrier
  const handleDelete = async () => {
    try {
      await fetch(`${apiUrl}/api/carriers/${deleteId}`, { method: "DELETE" });
      fetchCarriers(); // Refresh list
      setDeleteId(null); // Close confirmation modal
    } catch (err) {
      setError("Error deleting carrier. Please try again.");
    }
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-semibold">Carriers</h2>
        <button
          className="btn btn-primary"
          onClick={() => {
            setEditMode(false);
            setModalData({ name: "", code: "" });
          }}
        >
          Add Carrier
        </button>
      </div>

      {loading ? (
        <p>Loading carriers...</p>
      ) : error ? (
        <p className="text-red-500">{error}</p>
      ) : carriers.length === 0 ? (
        <p>No carriers available.</p>
      ) : (
        <table className="table w-full border-collapse border border-gray-300">
          <thead>
            <tr>
              <th className="border border-gray-300 bg-base-200">Name</th>
              <th className="border border-gray-300 bg-base-200">Created</th>
              <th className="border border-gray-300 bg-base-200">Actions</th>
            </tr>
          </thead>
          <tbody>
            {carriers.map((carrier) => (
              <tr key={carrier.id}>
                <td>{carrier.name}</td>
                <td>{formatDate(carrier.created_at)}</td>
                <td className="flex gap-2">
                  <button
                    className="btn btn-sm btn-secondary"
                    onClick={() => {
                      setEditMode(true);
                      setModalData(carrier);
                    }}
                  >
                    Edit
                  </button>
                  <button
                    className="btn btn-sm btn-danger"
                    onClick={() => setDeleteId(carrier.id)}
                  >
                    Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {/* Modal for Add/Edit */}
      {modalData && (
        <dialog className="modal modal-open bg-gray-50">
          <form
            className="modal-box"
            onSubmit={(e) => {
              e.preventDefault();
              handleSave(modalData);
            }}
          >
            <h3 className="font-bold text-lg">
              {editMode ? "Edit Carrier" : "Add Carrier"}
            </h3>
            <div className="form-control">
              <label htmlFor="name" className="label-text">
                Name
              </label>
              <input
                type="text"
                name="name"
                value={modalData.name}
                onChange={(e) =>
                  setModalData((prev) => ({
                    ...prev,
                    name: e.target.value,
                  }))
                }
                className="input input-bordered"
                required
              />
            </div>
            <div className="modal-action">
              <button
                type="button"
                className="btn"
                onClick={() => setModalData(null)}
              >
                Cancel
              </button>
              <button type="submit" className="btn btn-primary">
                <FaSave />
                {editMode ? "Update" : "Save"}
              </button>
            </div>
          </form>
        </dialog>
      )}

      {/* Confirmation Modal for Delete */}
      {deleteId && (
        <dialog className="modal modal-open">
          <div className="modal-box">
            <h3 className="font-bold text-lg">Confirm Deletion</h3>
            <p>
              Are you sure you want to delete this carrier? This action cannot
              be undone.
            </p>
            <div className="modal-action">
              <button
                className="btn"
                onClick={() => setDeleteId(null)}
              >
                Cancel
              </button>
              <button
                className="btn btn-danger"
                onClick={handleDelete}
              >
                Delete
              </button>
            </div>
          </div>
        </dialog>
      )}
    </div>
  );
};

export default Carriers;
